import React, { useEffect, useState, useRef, Fragment, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import StoreServices from '../../../services/Store';
import BayServices from '../../../services/Bay';
import SectionServices from '../../../services/Section';
import SaleInvoiceServices from '../../../services/SaleInvoice';
import ReportServices from '../../../services/Report';
import ProductServices from '../../../services/Product';
import BrandServices from '../../../services/Brand';
import CategoryServices from '../../../services/Category';
import { generatePdf } from '../../../utils/helper';
import { useForm, Controller } from 'react-hook-form';
import SelectField from '../../components/Select';
import InputField from '../../components/Input';
import { Select } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import PaymentHeadServices from '../../../services/PaymentHead';

/**
 * Dedicated Exchange Form Component
 * Handles exchange operations with specific fee calculations
 *
 * Exchange Scenarios:
 * - 10% fee deducted from original item value
 * - Pickup/delivery charges added
 * - Store credit = (original value - 10% fee - charges)
 * - New item price - store credit + tax for difference
 */
const ExchangeForm = ({ preselectedInvoices, preselectedItems, onSubmit, onClose }) => {
  // State
  const navigate = useNavigate();
  const [selectedInvoices, setSelectedInvoices] = useState(preselectedInvoices || []);
  const [selectedItems, setSelectedItems] = useState(preselectedItems || []);
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [itemActions, setItemActions] = useState({});

  // Data for dropdowns
  const [stores, setStores] = useState([]);
  const [bays, setBays] = useState([]);
  const [sections, setSections] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  // Estimate state for realtime calculations
  const [estimate, setEstimate] = useState({
    exchanges: [],
    totals: {
      storeCredit: 0,
      exchangeFees: 0,
      pickupCharges: 0,
      amountDue: 0,
      taxAmount: 0,
      totalDue: 0,
      taxPercentage: 0,
    },
  });

  // User-editable fees and charges
  const [userExchangeFees, setUserExchangeFees] = useState(0);
  const [userPickupCharges, setUserPickupCharges] = useState(0);

  // Payment method state - now supports multiple payment methods
  const [paymentEntries, setPaymentEntries] = useState([
    { id: uuidv4(), payment_head: null, card_last_digit: '', received_amount: '' }
  ]);

  // Payment methods data - now stateful to allow adding new methods
  const [paymentMethods, setPaymentMethods] = useState([]);

  // Store original calculated values
  const [originalTotals, setOriginalTotals] = useState({
    storeCredit: 0,
    exchangeFees: 0,
    pickupCharges: 0,
    amountDue: 0,
    taxAmount: 0,
    totalDue: 0,
  });

  // Debug logs - can be removed in production
  // console.log('Current estimate state:', estimate);
  // console.log('Original totals:', originalTotals);
  // console.log('User fees:', { userExchangeFees, userPickupCharges });
  // console.log('Item actions:', itemActions);
  // console.log('Should trigger calculation:', shouldTriggerCalculation);
  const [estimating, setEstimating] = useState(false);
  const invoiceRef = useRef(null);

  // Invoice search
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceResults, setInvoiceResults] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  // Product search for new items
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [productLoading, setProductLoading] = useState(false);

  // Product selector modal
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [currentItemId, setCurrentItemId] = useState(null);

  // Form state for product selection modal (similar to sales form)
  const { register: register2, handleSubmit: handleSubmit2, formState: { errors: errors2 }, control: control2, reset: reset2, setValue: setValue2, getValues: getValues2 } = useForm();

  // Additional state for product selection modal
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modalProducts, setModalProducts] = useState([]);
  const [modalVariants, setModalVariants] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedModalProduct, setSelectedModalProduct] = useState(null);
  const [selectedModalVariant, setSelectedModalVariant] = useState(null);
  const [selectedShippingType, setSelectedShippingType] = useState(null);

  // Fetch stores, bays, sections on mount (for main form)
  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      StoreServices.getStores({ active: true }),
      BayServices.getBays({ active: true }),
      SectionServices.getSections({ active: true })
    ]).then(([storeRes, bayRes, sectionRes]) => {
      setStores(storeRes.data || storeRes || []);
      setBays(bayRes.data || bayRes || []);
      setSections(sectionRes.data || sectionRes || []);
    }).catch(() => {
      setError('Failed to load store/bay/section data');
    }).finally(() => setLoading(false));
  }, []);

  // Fetch brands and categories when product selector modal opens
  useEffect(() => {
    if (showProductSelector) {
      Promise.all([
        BrandServices.getBrands({ active: true }),
        CategoryServices.getCategories({ active: true })
      ]).then(([brandRes, categoryRes]) => {
        setBrands(brandRes.data || brandRes || []);
        setCategories(categoryRes.data || categoryRes || []);
      }).catch(() => {
        setError('Failed to load brand/category data');
      });
    }
  }, [showProductSelector]);

  // Get products for modal
  const getModalProducts = useCallback(async () => {
    try {
      let params = {
        page: 1,
        limit: 50,
        active: true,
        brand: selectedBrand?.id || '',
        category: selectedCategory?.id || '',
      }
      const { data } = await ProductServices.getProducts(params)
      setModalProducts(data)
    } catch (error) {
      console.error('getModalProducts error:', error)
    }
  }, [selectedBrand?.id, selectedCategory?.id])

  // Handle variant selection
  const handleSelectVariant = (variantKey, variantValue) => {
    try {
      // Get all current variant selections
      const allValues = getValues2();
      const variantSelections = {};

      // Collect all variant selections
      Object.entries(allValues).forEach(([key, value]) => {
        if (key.includes('variant-') && value != null) {
          const attrKey = key.replace('variant-', '');
          variantSelections[attrKey] = value;
        }
      });

      // Update the current selection
      variantSelections[variantKey] = variantValue;

      if (selectedModalProduct?.variants && Object.keys(variantSelections).length > 0) {
        // Find matching variant by comparing all selected attributes
        const matchingVariant = selectedModalProduct.variants.find(variant => {
          const variantAttrs = variant?.attributes || {};
          const selectedKeys = Object.keys(variantSelections);

          // Check if all selected attributes match
          return selectedKeys.every(key => variantAttrs[key] === variantSelections[key]);
        });

        if (matchingVariant) {
          const price = matchingVariant?.price || 0;

          // Update form fields
          setValue2('regPrice', price);
          setValue2('salePrice', price);

          // Update selected variant state
          setSelectedModalVariant({
            id: matchingVariant?.id,
            name: Object.values(matchingVariant?.attributes || {}).map(String).join('-').toUpperCase(),
            price: price,
            totalOnHold: matchingVariant?.totalOnHold || 0,
            totalQuantity: matchingVariant?.totalQuantity || 0
          });
        } else {
          // Clear variant selection if no match
          setSelectedModalVariant(null);
          setValue2('regPrice', '');
          setValue2('salePrice', '');
        }
      } else if (Object.keys(variantSelections).length === 0) {
        // No variants selected, clear everything
        setSelectedModalVariant(null);
        setValue2('regPrice', '');
        setValue2('salePrice', '');
      }
    } catch (error) {
      console.error('handleSelectVariant error:', error);
    }
  }

  // Reset modal fields
  const resetModalFields = () => {
    try {
      // Reset the form
      reset2()

      // Clear state variables
      setModalVariants([])
      setSelectedBrand(null)
      setSelectedCategory(null)
      setSelectedModalProduct(null)
      setSelectedModalVariant(null)
      setSelectedShippingType(null)
    } catch (error) {
      console.error('resetModalFields error:', error)
    }
  }

  // Fetch items for preselectedInvoices if needed
  useEffect(() => {
    const needsFetch =
      (preselectedInvoices &&
        (
          !preselectedItems ||
          preselectedItems.length === 0 ||
          preselectedItems.some(item => !item.name)
        )
      );
    if (needsFetch) {
      setLoading(true);
      Promise.all(
        preselectedInvoices.map(inv =>
          SaleInvoiceServices.getSaleInvoiceById(inv.id).then(res => {
            const invoiceData = (res.data && res.data[0]) || res;
            return invoiceData && invoiceData.sale_invoice_items
              ? invoiceData.sale_invoice_items
                .filter(item => (item.quantity || 0) > 0)
                .map(item => ({
                  ...item,
                  availableQuantity: item.quantity || 0,
                  invoiceId: inv.id
                }))
              : [];
          })
        )
      )
        .then(results => {
          setSelectedItems(results.flat());
        })
        .catch(() => setSelectedItems([]))
        .finally(() => setLoading(false));
    } else if (preselectedItems && preselectedItems.length > 0) {
      const filtered = preselectedItems
        .filter(item => (item.quantity - (item.refunded_quantity || 0)) > 0)
        .map(item => ({
          ...item,
          availableQuantity: item.quantity - (item.refunded_quantity || 0),
        }));
      setSelectedItems(filtered);
    }
  }, [preselectedInvoices, preselectedItems]);

  // Handlers
  const handleActionChange = (itemId, field, value) => {
    setItemActions(prev => {
      const existing = prev[itemId] || {};
      return { ...prev, [itemId]: { ...existing, [field]: value } };
    });
  };

  const handleItemSelection = (itemId, isSelected) => {
    setSelectedItemIds(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(itemId);
      } else {
        newSet.delete(itemId);
        // Clear actions for unselected items
        setItemActions(prevActions => {
          const newActions = { ...prevActions };
          delete newActions[itemId];
          return newActions;
        });
      }
      return newSet;
    });
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      const allIds = selectedItems.map(item => item.id);
      setSelectedItemIds(new Set(allIds));
    } else {
      setSelectedItemIds(new Set());
      setItemActions({});
    }
  };

  // *For Get Payment Method
  const getPaymentHeads = async () => {
    try {
      let params = {
        page: 1,
        limit: 50,
        active: true
      }
      const { data } = await PaymentHeadServices.getPaymentHeads(params)
      setPaymentMethods(data)
    } catch (error) {
      console.log('getPaymentHeads => error:', error)
    }
  }

  useEffect(() => {
    getPaymentHeads();
  }, []);

  // Filter helpers
  const getBaysForStore = (storeId) => bays.filter(b => b.store_id === Number(storeId));
  const getSectionsForStoreBay = (storeId, bayId) => sections.filter(s => s.store_id === Number(storeId) && s.bay_id === Number(bayId));

  // Invoice search
  const handleInvoiceSearch = async () => {
    setInvoiceLoading(true);
    setError(null);
    try {
      const res = await SaleInvoiceServices.getSaleInvoices({ search: invoiceSearch, limit: 10 });
      setInvoiceResults(res.data || res || []);
    } catch (e) {
      setError('Failed to search invoices');
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleSelectInvoice = async (invoice) => {
    if (selectedInvoices.find(inv => inv.id === invoice.id)) return;
    setSelectedInvoices(prev => [...prev, invoice]);
    try {
      const res = await SaleInvoiceServices.getSaleInvoiceById(invoice.id);
      const invoiceData = (res.data && res.data[0]) || res;
      if (invoiceData && invoiceData.sale_invoice_items) {
        setSelectedItems(prev => ([...prev, ...invoiceData.sale_invoice_items.map(item => ({ ...item, invoiceId: invoice.id }))]));
      }
    } catch {
      setError('Failed to fetch invoice items');
    }
  };

  const handleRemoveInvoice = (invoiceId) => {
    setSelectedInvoices(prev => prev.filter(inv => inv.id !== invoiceId));
    setSelectedItems(prev => prev.filter(item => item.invoiceId !== invoiceId));
    setItemActions(prev => {
      const newActions = { ...prev };
      Object.keys(newActions).forEach(itemId => {
        const item = selectedItems.find(i => i.id === Number(itemId));
        if (item && item.invoiceId === invoiceId) delete newActions[itemId];
      });
      return newActions;
    });
  };

  // Product search for new items
  const handleProductSearch = async (searchTerm) => {
    if (!searchTerm) {
      setProductResults([]);
      return;
    }
    setProductLoading(true);
    try {
      const res = await ProductServices.getProducts({ search: searchTerm, limit: 10 });
      setProductResults(res.data || res || []);
    } catch (e) {
      setError('Failed to search products');
    } finally {
      setProductLoading(false);
    }
  };

  // Product selector handlers
  const handleOpenProductSelector = (itemId, data) => {
    setCurrentItemId(itemId);
    // Don't reset modal fields when opening if we already have data
    // Only reset if we're opening for a different item
    if (currentItemId !== itemId) {
      resetModalFields();

      // setValue2('brand', data?.item.brand?.name)
      // setValue2('category', data?.item.category?.name)
      setValue2('product', data?.newItemName)
      // setValue2('description', data?.item.description)
      setValue2('quantity', data?.quantity)
      setValue2('regPrice', data?.selectedVariant?.price)
      setValue2('salePrice', data?.newItemPrice)
      // setValue2('shippingType', data?.deliveryType?.name)
      // setSelectedBrand(data?.item.brand)
      // setSelectedCategory(data?.item.category)
      setSelectedModalProduct(data?.selectedProduct)
      setSelectedModalVariant(data?.selectedVariant)
      // setSelectedShippingType(data?.deliveryType)
    }
    setShowProductSelector(true);
  };

  const handleProductSelect = (product) => {
    if (currentItemId) {
      // Update all product-related fields
      const itemName = product.name || selectedModalProduct?.name || 'Unknown Item';
      const variantName = product.variantName || selectedModalVariant?.name || '';
      const displayName = `${itemName}${variantName ? ` (${variantName})` : ''}`;

      handleActionChange(currentItemId, 'newItemId', product.variantId || product.id);
      handleActionChange(currentItemId, 'newItemName', displayName);
      handleActionChange(currentItemId, 'newItemPrice', product.finalPrice || product.price || 0);
      handleActionChange(currentItemId, 'quantity', product.quantity || product.availableQuantity || 0);
      handleActionChange(currentItemId, 'selectedProduct', product);
      handleActionChange(currentItemId, 'selectedVariant', product.selectedVariant);
      handleActionChange(currentItemId, 'variantName', product.variantName);
      // Set item ID and variant ID separately
      handleActionChange(currentItemId, 'itemId', product.itemId || product.id);
      handleActionChange(currentItemId, 'variantId', product.variantId);

      // Force a re-render and trigger calculation update
      setItemActions(prev => ({ ...prev }));
    }
    setShowProductSelector(false);
    setCurrentItemId(null);
  };

  const handleCloseProductSelector = () => {
    // Don't reset form fields when closing, preserve the state
    setShowProductSelector(false);
    // Don't set currentItemId to null to preserve form state
    // setCurrentItemId(null);
  };

  // Invoice/item selection UI
  const renderInvoiceItemSelection = () => {
    if (preselectedInvoices && preselectedItems) return null;
    return (
      <div style={{ marginBottom: 20 }}>
        <div>
          <input
            type="text"
            placeholder="Search invoices..."
            value={invoiceSearch}
            onChange={e => setInvoiceSearch(e.target.value)}
            style={{ marginRight: 8 }}
          />
          <button onClick={handleInvoiceSearch} disabled={invoiceLoading || !invoiceSearch}>Search</button>
        </div>
        {invoiceLoading && <div>Searching...</div>}
        {invoiceResults.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <b>Results:</b>
            <ul>
              {invoiceResults.map(inv => (
                <li key={inv.id}>
                  #{inv.id} - {inv.order_number || inv.id} - {inv.billing_name} {inv.billing_last_name}
                  <button style={{ marginLeft: 8 }} onClick={() => handleSelectInvoice(inv)}>Select</button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {selectedInvoices.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <b>Selected Invoices:</b>
            <ul>
              {selectedInvoices.map(inv => (
                <li key={inv.id}>
                  #{inv.id} - {inv.order_number || inv.id} - {inv.billing_name} {inv.billing_last_name}
                  <button style={{ marginLeft: 8 }} onClick={() => handleRemoveInvoice(inv.id)}>Remove</button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Build table rows for exchange
  const renderTableRows = () => {
    if (!selectedItems.length) return <tr><td colSpan={8}>No items selected.</td></tr>;
    return selectedItems.map(item => {
      const action = itemActions[item.id] || {};
      const baysForStore = action.storeId ? getBaysForStore(action.storeId) : [];
      const sectionsForStoreBay = (action.storeId && action.bayId) ? getSectionsForStoreBay(action.storeId, action.bayId) : [];

      const itemObj = item.item || {};
      const variantObj = item.variant || {};
      const name = itemObj.name || item.name || item.item_name || 'Unnamed Item';
      let attrStr = '';
      if (variantObj.attributes && typeof variantObj.attributes === 'object') {
        attrStr = Object.entries(variantObj.attributes)
          .map(([k, v]) => `${k}: ${v}`)
          .join(' • ');
      } else if (item.variant_options && typeof item.variant_options === 'object') {
        attrStr = Object.entries(item.variant_options)
          .map(([k, vals]) => `${k}: ${Array.isArray(vals) ? vals.join('/') : vals}`)
          .join(' • ');
      }
      const displayName = attrStr ? `${name} — ${attrStr}` : name;

      return (
        <tr key={item.id}>
          <td style={{ padding: '6px 4px' }}>
            <input
              type="checkbox"
              checked={selectedItemIds.has(item.id)}
              onChange={(e) => handleItemSelection(item.id, e.target.checked)}
              style={{ margin: 0 }}
            />
          </td>
          <td style={{ padding: '6px 4px', fontWeight: 500, fontSize: 13 }}>{displayName}</td>
          <td style={{ padding: '6px 4px' }}>
            <select value={action.storeId || ''} onChange={e => handleActionChange(item.id, 'storeId', e.target.value)} disabled={!selectedItemIds.has(item.id)} style={{ width: '100%', padding: '4px', fontSize: '12px', borderRadius: '3px', border: '1px solid #ccc' }}>
              <option value=''>Select Store</option>
              {stores.map(store => (
                <option key={store.id} value={store.id}>{store.name}</option>
              ))}
            </select>
          </td>
          <td style={{ padding: '6px 4px' }}>
            <select value={action.bayId || ''} onChange={e => handleActionChange(item.id, 'bayId', e.target.value)} disabled={!selectedItemIds.has(item.id) || !action.storeId} style={{ width: '100%', padding: '4px', fontSize: '12px', borderRadius: '3px', border: '1px solid #ccc' }}>
              <option value=''>Select Bay</option>
              {baysForStore.map(bay => (
                <option key={bay.id} value={bay.id}>{bay.name}</option>
              ))}
            </select>
          </td>
          <td style={{ padding: '6px 4px' }}>
            <select value={action.sectionId || ''} onChange={e => handleActionChange(item.id, 'sectionId', e.target.value)} disabled={!selectedItemIds.has(item.id) || !action.storeId || !action.bayId} style={{ width: '100%', padding: '4px', fontSize: '12px', borderRadius: '3px', border: '1px solid #ccc' }}>
              <option value=''>Select Section</option>
              {sectionsForStoreBay.map(section => (
                <option key={section.id} value={section.id}>{section.name}</option>
              ))}
            </select>
          </td>
          <td style={{ padding: '6px 4px' }}>
            <input type='number' min={1} max={item.availableQuantity} value={action.quantity || item.availableQuantity} onChange={e => handleActionChange(item.id, 'quantity', e.target.value)} disabled={!selectedItemIds.has(item.id)} style={{ width: '100%', padding: '4px', fontSize: '12px', borderRadius: '3px', border: '1px solid #ccc' }} />
          </td>
          <td style={{ padding: '6px 4px' }}>
            {action.newItemName ? (
              <div>
                <div style={{
                  padding: '6px 8px',
                  background: '#f8f9fa',
                  border: '1px solid #28a745',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#28a745',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div>
                    <i className="fas fa-check-circle me-1"></i>
                    {action.newItemName}
                    <br />
                    <small style={{ color: '#666', fontWeight: 'normal' }}>
                      ${parseFloat(action.newItemPrice || 0).toFixed(2)}
                    </small>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleOpenProductSelector(item.id, action)}
                    disabled={!selectedItemIds.has(item.id)}
                    className="btn btn-sm btn-outline-success"
                    style={{ fontSize: '10px', padding: '2px 6px' }}
                    title="Change product"
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => handleOpenProductSelector(item.id)}
                disabled={!selectedItemIds.has(item.id)}
                className="btn btn-outline-success btn-sm"
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  fontSize: '12px',
                  border: '2px dashed #28a745'
                }}
              >
                <i className="fas fa-plus me-1"></i>
                Select Product
              </button>
            )}
          </td>
          <td style={{ padding: '6px 4px' }}>
            <input type='text' value={action.reason || ''} onChange={e => handleActionChange(item.id, 'reason', e.target.value)} disabled={!selectedItemIds.has(item.id)} style={{ width: '100%', padding: '4px', fontSize: '12px', borderRadius: '3px', border: '1px solid #ccc' }} />
          </td>
        </tr>
      );
    });
  };

  // Submit handler for exchanges
  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const exchangeMap = {};
      Object.entries(itemActions).forEach(([itemId, action]) => {
        const item = selectedItems.find(i => i.id === Number(itemId));
        if (!item || !action.newItemId) return;

        if (!exchangeMap[item.invoiceId]) exchangeMap[item.invoiceId] = { items: [], notes: action.reason };
        exchangeMap[item.invoiceId].items.push({
          originalItemId: item.item_id || item.id,
          saleInvoiceItemId: item.id,
          newItemId: Number(action.itemId),
          variantId: Number(item.variant_id),
          newVariantId: Number(action.variantId),
          newItemPrice: Number(action.newItemPrice || 0),
          quantity: Number(action.quantity),
          storeId: Number(action.storeId),
          bayId: Number(action.bayId),
          sectionId: Number(action.sectionId),
        });
        exchangeMap[item.invoiceId].notes = action.reason;
      });

      // Map payment entries to DTO format
      const validPayments = paymentEntries.filter(entry =>
        entry.payment_head &&
        entry.received_amount &&
        parseFloat(entry.received_amount) > 0
      ).map(entry => ({
        payment_head_id: entry.payment_head.id,
        received_amount: parseFloat(entry.received_amount),
        payment_ref: entry.card_last_digit || ''
      }));

      const exchanges = Object.entries(exchangeMap).map(([invoiceId, data]) => ({
        originalTransactionId: Number(invoiceId),
        items: data.items,
        notes: data.notes,
        userExchangeFee: userExchangeFees,
        userPickupCharge: userPickupCharges,
        invoice_payments: validPayments,
      }));

      const payload = { refunds: [], exchanges };
      const res = await ReportServices.createRefundExchange(payload);
      setSuccess('Exchange submitted successfully!');
      if (onSubmit) onSubmit(payload, res);
    } catch (e) {
      setError('Failed to submit exchange');
    } finally {
      setSubmitting(false);
    }
  };

  // Print estimate / preview
  const handlePrintEstimate = async () => {
    if (!invoiceRef?.current) {
      console.error('Invoice ref is not available');
      return;
    }

    console.log('Starting PDF generation for exchange estimate');
    console.log('Invoice ref content:', invoiceRef.current);
    console.log('Invoice ref innerHTML:', invoiceRef.current.innerHTML);

    try {
      await generatePdf([invoiceRef], 'exchange-estimate');
      console.log('PDF generation completed successfully');
    } catch (err) {
      console.error('Failed to generate PDF estimate', err);
    }
  };

  // Server-side estimate fetcher for exchanges
  const fetchServerEstimate = useCallback(async () => {
    setEstimating(true);
    try {
      const ia = itemActions || {};
      const si = selectedItems || [];

      const exchangeMap = {};
      selectedItemIds.forEach(itemId => {
        const item = si.find(i => i.id === Number(itemId));
        const action = ia[itemId];

        if (!item || !action || !action.newItemId) {
          return;
        }

        if (!exchangeMap[item.invoiceId]) exchangeMap[item.invoiceId] = { items: [], notes: action.reason };
        exchangeMap[item.invoiceId].items.push({
          originalItemId: item.item_id || item.id,
          saleInvoiceItemId: item.id,
          newItemId: Number(action.itemId),
          variantId: Number(action.variantId),
          newItemPrice: Number(action.newItemPrice || 0),
          quantity: Number(action.quantity || item.availableQuantity || 1),
          storeId: Number(action.storeId || 0),
          bayId: Number(action.bayId || 0),
          sectionId: Number(action.sectionId || 0),
        });
      });

      const exchanges = Object.entries(exchangeMap).map(([invoiceId, data]) => ({
        originalTransactionId: Number(invoiceId),
        items: data.items,
        notes: data.notes,
      }));

      userExchangeFees != 0 ? exchanges.userExchangeFee = userExchangeFees : null;
      userPickupCharges != 0 ? exchanges.userPickupCharge = userPickupCharges : null;

      console.log('Exchange payload:', { refunds: [], exchanges });

      if (exchanges.length > 0) {
        const payload = { refunds: [], exchanges };

        const res = await ReportServices.calculateRefundExchange(payload);
        const data = res?.data ?? res;
        console.log('Exchange calculation response:', data);

        if (data) {
          // Simple direct mapping - assume backend returns expected structure
          const mappedData = {
            ...data,
            totals: {
              storeCredit: data.totals?.storeCredit || data.totals?.exchangeStoreCredit || 0,
              exchangeFees: data.totals?.exchangeFees || 0,
              pickupCharges: data.totals?.pickupCharges || 0,
              amountDue: data.totals?.amountDue || data.totals?.exchangeAmountDue || 0,
              taxAmount: data.totals?.taxAmount || data.totals?.exchangeTax || 0,
              totalDue: data.totals?.totalDue || data.totals?.exchangeTotalDue || 0,
              taxPercentage: data.totals?.taxPercentage || data.totals?.exchangeTaxPercentage || 0,
            }
          };

          setEstimate(mappedData);
          setOriginalTotals(mappedData.totals);
          // Set user-editable fees to calculated values initially
          setUserExchangeFees(Number(mappedData.totals.exchangeFees) || 0);
          setUserPickupCharges(Number(mappedData.totals.pickupCharges) || 0);
        } else {
          const defaultEstimate = { exchanges: [], totals: { storeCredit: 0, exchangeFees: 0, pickupCharges: 0, amountDue: 0, taxAmount: 0, totalDue: 0 } };
          setEstimate(defaultEstimate);
          setOriginalTotals({ storeCredit: 0, exchangeFees: 0, pickupCharges: 0, amountDue: 0, taxAmount: 0, totalDue: 0 });
          setUserExchangeFees(0);
          setUserPickupCharges(0);
        }
      } else {
        setEstimate({ exchanges: [], totals: { storeCredit: 0, exchangeFees: 0, pickupCharges: 0, amountDue: 0, taxAmount: 0, totalDue: 0 } });
        setOriginalTotals({ storeCredit: 0, exchangeFees: 0, pickupCharges: 0, amountDue: 0, taxAmount: 0, totalDue: 0 });
        setUserExchangeFees(0);
        setUserPickupCharges(0);
      }
    } catch (err) {
      console.error('fetchServerEstimate error', err);
      setEstimate({ exchanges: [], totals: { storeCredit: 0, exchangeFees: 0, pickupCharges: 0, amountDue: 0, taxAmount: 0, totalDue: 0 } });
      setOriginalTotals({ storeCredit: 0, exchangeFees: 0, pickupCharges: 0, amountDue: 0, taxAmount: 0, totalDue: 0 });
      setUserExchangeFees(0);
      setUserPickupCharges(0);
    } finally {
      setEstimating(false);
    }
  }, [itemActions, selectedItems, userExchangeFees, userPickupCharges]);

  // Memoize the total new item price calculation
  const totalNewItemPrice = useMemo(() => {
    return Object.values(itemActions).reduce((total, action) => {
      if (action && action.newItemPrice && action.quantity) {
        return total + (parseFloat(Number(action.newItemPrice)) * parseInt(action.quantity));
      }
      return total;
    }, 0);
  }, [itemActions]);

  // Function to recalculate totals based on user-editable fees
  const recalculateTotals = useCallback(() => {
    const originalValue = originalTotals.storeCredit + originalTotals.exchangeFees + originalTotals.pickupCharges;
    const newStoreCredit = Math.max(0, originalValue - userExchangeFees - userPickupCharges);
    const newItemsTotal = totalNewItemPrice > 0 ? totalNewItemPrice : 0;
    const newAmountDueBeforeTax = Math.max(0, newItemsTotal - newStoreCredit);
    const taxPercentage = originalTotals.amountDue > 0 ? originalTotals.taxAmount / originalTotals.amountDue : 0;
    const newTaxAmount = Number((newAmountDueBeforeTax * taxPercentage).toFixed(2));
    const newTotalDue = newAmountDueBeforeTax + newTaxAmount;

    setEstimate(prev => ({
      ...prev,
      totals: {
        ...prev.totals,
        storeCredit: newStoreCredit,
        exchangeFees: userExchangeFees,
        pickupCharges: userPickupCharges,
        amountDue: newAmountDueBeforeTax,
        taxAmount: newTaxAmount,
        totalDue: newTotalDue,
        taxPercentage: originalTotals.taxPercentage,
      }
    }));
  }, [originalTotals, userExchangeFees, userPickupCharges, totalNewItemPrice]);

  // Effect to recalculate when user fees change, original totals are set, or items change
  useEffect(() => {
    // Recalculate when we have data or when user changes fees
    if ((originalTotals.storeCredit > 0 || totalNewItemPrice > 0) ||
      (userExchangeFees !== 0 || userPickupCharges !== 0)) {
      recalculateTotals();
    }
  }, [userExchangeFees, userPickupCharges, originalTotals.storeCredit, totalNewItemPrice, recalculateTotals]);

  // Memoize the calculation trigger to prevent unnecessary re-executions
  const shouldTriggerCalculation = useMemo(() => {
    return selectedItemIds.size > 0;
  }, [selectedItemIds]);

  // Debounced effect to call server estimate
  useEffect(() => {
    const t = setTimeout(() => {
      if (shouldTriggerCalculation) {
        fetchServerEstimate();
      } else {
        setEstimate({ exchanges: [], totals: { storeCredit: 0, exchangeFees: 0, pickupCharges: 0, amountDue: 0, taxAmount: 0, totalDue: 0 } });
        setOriginalTotals({ storeCredit: 0, exchangeFees: 0, pickupCharges: 0, amountDue: 0, taxAmount: 0, totalDue: 0 });
        setUserExchangeFees(0);
        setUserPickupCharges(0);
      }
    }, 500);

    return () => clearTimeout(t);
  }, [shouldTriggerCalculation, fetchServerEstimate, selectedItemIds]);

  // Load products when modal opens
  useEffect(() => {
    if (showProductSelector) {
      getModalProducts()
      setSelectedModalProduct(null)
    }
  }, [showProductSelector, getModalProducts]);

  // Handle cascading dropdowns for modal
  useEffect(() => {
    if (showProductSelector && (selectedBrand?.id || selectedCategory?.id)) {
      getModalProducts()
      setSelectedModalProduct(null)
    }
  }, [selectedBrand, selectedCategory, showProductSelector, getModalProducts]);

  useEffect(() => {
    if (selectedModalProduct?.id) {
      const variant = []
      if (selectedModalProduct?.variant_options) {
        Object.entries(selectedModalProduct?.variant_options).map(([key, value]) => {
          variant.push({
            name: key,
            options: value.map((e) => { return { label: e, value: e } }),
          })
        })
      }
      setModalVariants(variant)
      setSelectedModalVariant(null)
    }
  }, [selectedModalProduct]);

  useEffect(() => {
    if (currentItemId) {
      // Only set brand, category, product, and other basic fields
      // Don't override regPrice and salePrice if they were set by variant selection
      setValue2('brand', selectedBrand?.name || '')
      setValue2('category', selectedCategory?.name || '')
      setValue2('product', selectedModalProduct?.name || '')
      setValue2('description', '')
      setValue2('quantity', 1)
      setValue2('shippingType', selectedShippingType?.name || '')

      // Only set prices if variant is selected and has a price, and prices are not already set
      const currentRegPrice = getValues2('regPrice');
      const currentSalePrice = getValues2('salePrice');

      if (selectedModalVariant?.price && (!currentRegPrice || currentRegPrice === '')) {
        setValue2('regPrice', selectedModalVariant.price);
        setValue2('salePrice', selectedModalVariant.price);
      }

      if (selectedModalProduct?.variants?.length > 0) {
        const variant = [];
        if (selectedModalProduct?.variant_options) {
          Object.entries(selectedModalProduct?.variant_options).map(([key, value]) => {
            variant.push({
              name: key,
              options: value.map((e) => { return { label: e, value: e } }),
            })
          })
        }
        setModalVariants(variant);
        // Don't reset selectedModalVariant here if it was already set
        if (!selectedModalVariant) {
          setSelectedModalVariant(null);
        }
      }
    }
  }, [currentItemId, selectedBrand, selectedCategory, selectedModalProduct, selectedModalVariant, selectedShippingType]);

  // Render component
  return (
    <div className="card" style={{ border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
      <div className="card-header" style={{
        background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
        color: 'white',
        borderBottom: 'none',
        borderRadius: '8px 8px 0 0',
        padding: '20px'
      }}>
        <div className="d-flex align-items-center">
          <i className="fas fa-exchange-alt me-3" style={{ fontSize: '24px' }}></i>
          <div>
            <h4 className="mb-1" style={{ fontSize: '24px', fontWeight: '700' }}>Exchange Request</h4>
            <p className="mb-0" style={{ opacity: 0.9 }}>
              Process exchange for invoice #{preselectedInvoices && preselectedInvoices[0]?.order_number}
            </p>
          </div>
        </div>
      </div>

      <div className="card-body" style={{ padding: '20px' }}>
        {/* Invoice Information Section - Compact Layout */}
        {preselectedInvoices && preselectedInvoices.length > 0 && (
          <div className="row mb-3">
            <div className="col-12">
              <div className="card" style={{ border: '1px solid #e9ecef', borderRadius: '6px' }}>
                <div className="card-header" style={{
                  background: '#f8f9fa',
                  borderBottom: '1px solid #dee2e6',
                  borderRadius: '5px 5px 0 0',
                  padding: '10px 15px'
                }}>
                  <h6 className="mb-0" style={{ color: '#495057', fontWeight: '600', fontSize: '14px' }}>
                    <i className="fas fa-file-invoice me-2"></i>
                    Original Invoice Information
                  </h6>
                </div>
                <div className="card-body" style={{ padding: '15px' }}>
                  <div className="row">
                    <div className="col-lg-4 col-md-6">
                      <div className="mb-2">
                        <label className="form-label fw-bold text-muted" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                          Order Number
                        </label>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#007bff' }}>
                          {preselectedInvoices[0].order_number || preselectedInvoices[0].id}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <div className="mb-2">
                        <label className="form-label fw-bold text-muted" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                          Order Date
                        </label>
                        <div style={{ fontSize: '14px', fontWeight: '600' }}>
                          {preselectedInvoices[0].order_date ? new Date(preselectedInvoices[0].order_date).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <div className="mb-2">
                        <label className="form-label fw-bold text-muted" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                          Total Amount
                        </label>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#28a745' }}>
                          ${parseFloat(preselectedInvoices[0].total_sale || 0).toFixed(2)}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <div className="mb-2">
                        <label className="form-label fw-bold text-muted" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                          Customer Name
                        </label>
                        <div style={{ fontSize: '14px', fontWeight: '600' }}>
                          {preselectedInvoices[0].billing_name} {preselectedInvoices[0].billing_last_name}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <div className="mb-2">
                        <label className="form-label fw-bold text-muted" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                          Delivery Status
                        </label>
                        <div style={{ fontSize: '14px', fontWeight: '600' }}>
                          <span className={`badge ${preselectedInvoices[0].status?.toLowerCase().includes('deliv') ? 'badge-success' : 'badge-warning'}`} style={{ fontSize: '11px', padding: '3px 6px' }}>
                            {preselectedInvoices[0].status || 'Unknown'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <div className="mb-2">
                        <label className="form-label fw-bold text-muted" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                          Contact
                        </label>
                        <div style={{ fontSize: '13px' }}>
                          <i className="fas fa-phone me-1"></i>
                          {preselectedInvoices[0].billing_phone_number_one}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Items Section - Full Width */}
        <div className="row mb-4">
          <div className="col-lg-12">
            <div className="card" style={{ border: '1px solid #28a745', borderRadius: '6px' }}>
              <div className="card-header" style={{
                background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                color: 'white',
                borderBottom: 'none',
                borderRadius: '5px 5px 0 0',
                padding: '10px 15px'
              }}>
                <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>
                  <i className="fas fa-list me-2"></i>
                  Items to Exchange
                </h6>
              </div>
              <div className="card-body" style={{ padding: '15px' }}>
                <div className="alert alert-success" style={{ borderRadius: '4px', border: 'none', padding: '8px 12px', fontSize: '12px', marginBottom: '15px' }}>
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>Exchange Policy: </strong>
                  {
                    preselectedInvoices && preselectedInvoices[0]?.status?.toLowerCase().includes('deliv')
                      ? '10% processing fee applied to original items. Store credit generated after fees and pickup charges.<br/>New item cost minus store credit determines amount due.'
                      : 'No fees for undelivered items.'
                  }
                </div>
                {renderInvoiceItemSelection()}
                <div style={{ overflowX: 'auto', minHeight: 200, marginBottom: 15 }}>
                  <table border="0" cellPadding="0" style={{ width: '100%', minWidth: 900, tableLayout: 'fixed', background: '#fff', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <thead style={{ background: '#f8f9fa' }}>
                      <tr>
                        <th style={{ width: 40, padding: '8px 6px', fontWeight: 500, fontSize: 13, borderBottom: '1px solid #dee2e6' }}>
                          <input
                            type="checkbox"
                            checked={selectedItemIds.size === selectedItems.length && selectedItems.length > 0}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            style={{ margin: 0 }}
                          />
                        </th>
                        <th style={{ width: 180, padding: '8px 6px', fontWeight: 500, fontSize: 13, borderBottom: '1px solid #dee2e6' }}>Original Item</th>
                        <th style={{ width: 120, padding: '8px 6px', fontWeight: 500, fontSize: 13, borderBottom: '1px solid #dee2e6' }}>Store</th>
                        <th style={{ width: 100, padding: '8px 6px', fontWeight: 500, fontSize: 13, borderBottom: '1px solid #dee2e6' }}>Bay</th>
                        <th style={{ width: 120, padding: '8px 6px', fontWeight: 500, fontSize: 13, borderBottom: '1px solid #dee2e6' }}>Section</th>
                        <th style={{ width: 80, padding: '8px 6px', fontWeight: 500, fontSize: 13, borderBottom: '1px solid #dee2e6' }}>Qty</th>
                        <th style={{ width: 180, padding: '8px 6px', fontWeight: 500, fontSize: 13, borderBottom: '1px solid #dee2e6' }}>New Item</th>
                        <th style={{ width: 150, padding: '8px 6px', fontWeight: 500, fontSize: 13, borderBottom: '1px solid #dee2e6' }}>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedItems.length === 0 ? (
                        <tr><td colSpan={8} style={{ textAlign: 'center', padding: 20, color: '#aaa', fontSize: 14 }}>No items selected.</td></tr>
                      ) : renderTableRows()}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Calculation Summary - Centered Below */}
        <div className="row">
          <div className="col-lg-6 offset-lg-6">
            <div className="card" style={{ border: '1px solid #28a745', borderRadius: '6px', position: 'sticky', top: '20px' }}>
              <div className="card-header" style={{
                background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                color: 'white',
                borderBottom: 'none',
                borderRadius: '5px 5px 0 0',
                padding: '10px 15px'
              }}>
                <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>
                  <i className="fas fa-exchange-alt me-2"></i>
                  Exchange Summary
                  {/* Debug buttons - remove in production */}
                  {/* <button
                    onClick={() => fetchServerEstimate()}
                    style={{
                      marginLeft: '10px',
                      fontSize: '10px',
                      padding: '2px 6px',
                      background: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px'
                    }}
                  >
                    Test API
                  </button>
                  <button
                    onClick={() => {
                      const testData = {
                        exchanges: [],
                        totals: {
                          storeCredit: 100,
                          exchangeFees: 10,
                          pickupCharges: 5,
                          amountDue: 150,
                          taxAmount: 15,
                          totalDue: 70,
                        }
                      };
                      setEstimate(testData);
                      setOriginalTotals(testData.totals);
                      setUserExchangeFees(testData.totals.exchangeFees);
                      setUserPickupCharges(testData.totals.pickupCharges);
                    }}
                    style={{
                      marginLeft: '5px',
                      fontSize: '10px',
                      padding: '2px 6px',
                      background: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px'
                    }}
                  >
                    Test Data
                  </button> */}
                </h6>
              </div>
              <div className="card-body" style={{ padding: '15px' }}>
                {/* Store Credit Section */}
                <div style={{ marginBottom: '15px' }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
                    padding: '10px',
                    borderRadius: '4px',
                    border: '1px solid #28a745',
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span style={{
                        fontSize: '13px',
                        fontWeight: '700',
                        color: '#155724'
                      }}>
                        <i className="fas fa-credit-card me-1"></i>
                        STORE CREDIT
                      </span>
                      <span style={{
                        fontSize: '15px',
                        fontWeight: '800',
                        color: '#28a745'
                      }}>
                        ${Number(estimate?.totals?.storeCredit || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Itemized Breakdown */}
                <div style={{ marginBottom: '15px' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '5px 0',
                    borderBottom: '1px solid #f0f0f0',
                    fontSize: '12px'
                  }}>
                    <span style={{ color: '#666' }}>Original Value</span>
                    <span style={{ fontWeight: '500' }}>
                      ${Number(originalTotals.storeCredit + originalTotals.exchangeFees + originalTotals.pickupCharges || 0).toFixed(2)}
                    </span>
                  </div>

                  {
                    preselectedInvoices && preselectedInvoices[0]?.status?.toLowerCase().includes('deliv') ? (
                      <>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '5px 0',
                          borderBottom: '1px solid #f0f0f0',
                          color: '#e74c3c',
                          fontSize: '12px'
                        }}>
                          <span>Exchange Fees (10%)</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={userExchangeFees}
                            onChange={(e) => setUserExchangeFees(parseFloat(e.target.value) || 0)}
                            style={{
                              width: '80px',
                              padding: '2px 4px',
                              fontSize: '12px',
                              border: '1px solid #ccc',
                              borderRadius: '3px',
                              textAlign: 'right'
                            }}
                          />
                        </div>

                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '5px 0',
                          borderBottom: '1px solid #f0f0f0',
                          color: '#e74c3c',
                          fontSize: '12px'
                        }}>
                          <span>Pickup Charges</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={userPickupCharges}
                            onChange={(e) => setUserPickupCharges(parseFloat(e.target.value) || 0)}
                            style={{
                              width: '80px',
                              padding: '2px 4px',
                              fontSize: '12px',
                              border: '1px solid #ccc',
                              borderRadius: '3px',
                              textAlign: 'right'
                            }}
                          />
                        </div>
                      </>
                    ) : null
                  }
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '5px 0',
                    borderBottom: '1px solid #28a745',
                    fontSize: '12px'
                  }}>
                    <span style={{ color: '#666' }}>Store Credit</span>
                    <span style={{ fontWeight: '500', color: '#28a745' }}>
                      ${Number(estimate.totals.storeCredit || 0).toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* New Item Cost Section */}
                <div style={{ marginBottom: '15px' }}>
                  <h6 style={{ color: '#495057', marginBottom: '8px', fontWeight: '600', fontSize: '13px' }}>
                    <i className="fas fa-shopping-cart me-1"></i>
                    New Item Cost
                  </h6>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '5px 0',
                    borderBottom: '1px solid #f0f0f0',
                    fontSize: '12px'
                  }}>
                    <span style={{ color: '#666' }}>New Item Total</span>
                    <span style={{ fontWeight: '500' }}>
                      ${Number(totalNewItemPrice || 0).toFixed(2)}
                    </span>
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '5px 0',
                    borderBottom: '1px solid #f0f0f0',
                    fontSize: '12px'
                  }}>
                    <span style={{ color: '#666' }}>Store Credit Applied</span>
                    <span style={{ fontWeight: '500', color: '#28a745' }}>
                      -${Number(estimate.totals.storeCredit || 0).toFixed(2)}
                    </span>
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '5px 0',
                    borderBottom: '1px solid #f0f0f0',
                    fontSize: '12px'
                  }}>
                    <span style={{ color: '#666' }}>Subtotal</span>
                    <span style={{ fontWeight: '500' }}>
                      ${Number(Math.max(0, (totalNewItemPrice || 0) - (estimate.totals.storeCredit || 0))).toFixed(2)}
                    </span>
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '5px 0',
                    borderBottom: '1px solid #f0f0f0',
                    fontSize: '12px'
                  }}>
                    <span style={{ color: '#666' }}>Tax Amount ({Number(estimate.totals.taxPercentage || 0)}%)</span>
                    <span style={{ fontWeight: '500' }}>
                      ${Number(estimate.totals.taxAmount || 0).toFixed(2)}
                    </span>
                  </div>
                </div>


                {/* Payment Information - Only show if amount due > 0 */}
                {estimate.totals.totalDue > 0 && (
                  <>
                    <hr style={{ border: '1px solid #dee2e6', margin: '15px 0' }} />
                    <div style={{ marginBottom: '15px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h6 style={{ color: '#495057', marginBottom: '0', fontWeight: '600', fontSize: '13px' }}>
                          <i className="fas fa-credit-card me-1"></i>
                          Payment Info
                        </h6>
                        <button
                          type="button"
                          onClick={() => setPaymentEntries(prev => [...prev, { id: uuidv4(), payment_head: null, card_last_digit: '', received_amount: '' }])}
                          className="btn btn-outline-primary btn-sm"
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                        >
                          <i className="fas fa-plus me-1"></i>
                          Add New Payment
                        </button>
                      </div>
                      {paymentEntries.map((entry, index) => (
                        <div key={entry.id} style={{ border: '1px solid #ddd', padding: '10px', marginBottom: '10px', borderRadius: '4px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <h6 style={{ margin: 0, fontSize: '13px', fontWeight: '600' }}>Payment Entry {index + 1}</h6>
                            {paymentEntries.length > 1 && (
                              <button
                                type="button"
                                onClick={() => setPaymentEntries(prev => prev.filter(ent => ent.id !== entry.id))}
                                className="btn btn-outline-danger btn-sm"
                                style={{ fontSize: '10px', padding: '2px 6px' }}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            )}
                          </div>
                          <div className="row">
                            <div className="col-12">
                              <div className="mb-3">
                                <label className="form-label" style={{ fontSize: '12px', fontWeight: '600' }}>Payment Method</label>
                                <select
                                  value={entry.payment_head?.id || ''}
                                  onChange={(e) => {
                                    const payment_head = paymentMethods.find(m => m.id === Number(e.target.value));
                                    setPaymentEntries(prev => prev.map(ent => ent.id === entry.id ? { ...ent, payment_head } : ent));
                                  }}
                                  style={{ width: '100%', padding: '6px', fontSize: '12px', borderRadius: '3px', border: '1px solid #ccc' }}
                                >
                                  <option value=''>Select Payment Method</option>
                                  {paymentMethods.map(method => (
                                    <option key={method.id} value={method.id}>{method.name}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            {entry.payment_head?.name === 'Credit Card' && (
                              <div className="col-12">
                                <div className="mb-3">
                                  <label className="form-label" style={{ fontSize: '12px', fontWeight: '600' }}>Card Last Four</label>
                                  <input
                                    type="text"
                                    value={entry.card_last_digit}
                                    onChange={(e) => setPaymentEntries(prev => prev.map(ent => ent.id === entry.id ? { ...ent, card_last_digit: e.target.value } : ent))}
                                    maxLength={4}
                                    placeholder="1234"
                                    style={{ width: '100%', padding: '6px', fontSize: '12px', borderRadius: '3px', border: '1px solid #ccc' }}
                                  />
                                </div>
                              </div>
                            )}
                            <div className="col-12">
                              <div className="mb-3">
                                <label className="form-label" style={{ fontSize: '12px', fontWeight: '600' }}>Pay Amount</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={entry.received_amount}
                                  onChange={(e) => setPaymentEntries(prev => prev.map(ent => ent.id === entry.id ? { ...ent, received_amount: e.target.value } : ent))}
                                  placeholder="0.00"
                                  style={{ width: '100%', padding: '6px', fontSize: '12px', borderRadius: '3px', border: '1px solid #ccc' }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Final Total */}
                <div style={{
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                  padding: '10px',
                  borderRadius: '4px',
                  border: '1px solid #28a745',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{
                      fontSize: '13px',
                      fontWeight: '700',
                      color: '#28a745'
                    }}>
                      NET AMOUNT DUE
                    </span>
                    <span style={{
                      fontSize: '15px',
                      fontWeight: '800',
                      color: estimate.totals.totalDue > 0 ? '#e74c3c' : '#28a745'
                    }}>
                      ${Number(estimate.totals.totalDue || 0).toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Fee Explanation */}
                <div style={{
                  padding: '6px',
                  backgroundColor: '#d1ecf1',
                  border: '1px solid #bee5eb',
                  borderRadius: '3px',
                  fontSize: '10px',
                  color: '#0c5460',
                  lineHeight: '1.3',
                  marginBottom: '15px'
                }}>
                  <i className="fas fa-info-circle me-1"></i>
                  <strong>Policy:</strong> {
                    preselectedInvoices && preselectedInvoices[0]?.status?.toLowerCase().includes('deliv')
                      ? '10% fee applied to original items. Store credit generated after fees and pickup charges.<br/>New item cost minus store credit determines amount due.'
                      : 'No fees for undelivered items.'
                  }
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* Global loader overlay */}
        {(loading || submitting || estimating) && (
          <div id="global-loader" className="page-loader" style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.7)'
          }}>
            <div className="whirly-loader"></div>
          </div>
        )}


        {/* Action buttons */}
        <div className="row mt-3">
          <div className="col-lg-12">
            <div className="d-flex justify-content-between align-items-center">
              <button onClick={onClose} className="btn btn-outline-secondary btn-sm">
                <i className="fas fa-arrow-left me-1"></i>
                Back to Invoice
              </button>
              <div className="d-flex gap-2">
                {/* <button onClick={handlePrintEstimate} className="btn btn-outline-success btn-sm" disabled={estimating}>
                  <i className="fas fa-print me-1"></i>
                  {estimating ? 'Generating...' : 'Print Estimate'}
                </button> */}
                <button onClick={handleSubmit} className="btn btn-success" disabled={submitting}>
                  <i className="fas fa-exchange-alt me-1"></i>
                  {submitting ? 'Submitting...' : 'Submit Exchange'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden estimate for printing */}
      <div style={{ display: 'none' }}>
        <div ref={invoiceRef} style={{
          width: '950px',
          minHeight: '600px',
          padding: '24px',
          fontFamily: 'Arial, sans-serif',
          color: '#222',
          backgroundColor: '#ffffff',
          fontSize: '14px',
          lineHeight: '1.4',
          position: 'relative'
        }}>
          <h2 style={{ marginBottom: '20px', color: '#333', fontSize: '24px' }}>EXCHANGE ESTIMATE</h2>
          {selectedInvoices[0] ? (
            <div style={{ marginBottom: '20px' }}>
              <p style={{ margin: '5px 0' }}><strong>Order:</strong> {selectedInvoices[0].order_number || selectedInvoices[0].id}</p>
              <p style={{ margin: '5px 0' }}><strong>Date:</strong> {selectedInvoices[0].order_date ? new Date(selectedInvoices[0].order_date).toLocaleDateString() : ''}</p>
              <p style={{ margin: '5px 0' }}><strong>Customer:</strong> {selectedInvoices[0].billing_name} {selectedInvoices[0].billing_last_name}</p>
            </div>
          ) : null}
          <hr style={{ border: '1px solid #ccc', margin: '20px 0' }} />
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ marginBottom: '15px', color: '#333', fontSize: '18px' }}>Exchange Summary</h4>
            <p style={{ margin: '8px 0' }}>Store Credit: ${Number(estimate.totals.storeCredit || 0).toFixed(2)}</p>
            <p style={{ margin: '8px 0' }}>Exchange Fees: ${Number(estimate.totals.exchangeFees || 0).toFixed(2)}</p>
            <p style={{ margin: '8px 0' }}>Pickup Charges: ${Number(estimate.totals.pickupCharges || 0).toFixed(2)}</p>
            <p style={{ margin: '8px 0' }}>Amount Due: ${Number(estimate.totals.amountDue || 0).toFixed(2)}</p>
            <p style={{ margin: '8px 0' }}>Tax Amount: ${Number(estimate.totals.taxAmount || 0).toFixed(2)}</p>
            <p style={{ margin: '8px 0', fontWeight: 'bold', fontSize: '16px' }}>Total Due: ${Number(estimate.totals.totalDue || 0).toFixed(2)}</p>
          </div>
          <hr style={{ border: '1px solid #ccc', margin: '20px 0' }} />
          <p style={{ fontSize: '12px', color: '#666', margin: '10px 0' }}>This is an estimate. Final amounts may vary when processed.</p>
        </div>
      </div>

      {/* Product Selection Modal - Similar to Sales Form */}
      {showProductSelector && createPortal(
        <div className="modal fade show" style={{ display: 'block', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered custom-modal-two" style={{ maxWidth: '900px', margin: 'auto', position: 'relative', zIndex: 100000 }}>
            <div className="modal-content" style={{ zIndex: 100001 }}>
              <div className="page-wrapper-new p-0">
                <div className="content">
                  <div className="modal-header border-0 custom-modal-header">
                    <div className="page-title">
                      <h4>Select New Product for Exchange</h4>
                    </div>
                    <button
                      type="button"
                      className="close"
                      aria-label="Close"
                      onClick={handleCloseProductSelector}
                    >
                      <span aria-hidden="true">×</span>
                    </button>
                  </div>
                  <div className="modal-body custom-modal-body new-employee-field">
                    <form onSubmit={handleSubmit2((formData) => {
                      // Handle form submission for product selection
                      const selectedProduct = {
                        id: selectedModalProduct?.id,
                        name: selectedModalProduct?.name,
                        itemId: selectedModalProduct?.id,
                        variantId: selectedModalVariant?.id,
                        selectedVariant: selectedModalVariant,
                        variantName: selectedModalVariant?.name,
                        finalPrice: formData.salePrice || formData.regPrice,
                        price: formData.regPrice,
                        quantity: formData.quantity || 1
                      };

                      handleProductSelect(selectedProduct);
                    })}>
                      <div className="row">
                        <div className="col-lg-4 col-sm-6 col-12">
                          <div className="mb-3">
                            <SelectField
                              label={"Brand"}
                              options={brands}
                              selected={selectedBrand}
                              onSelect={(value) => setSelectedBrand(value)}
                              error={errors2?.brand?.message}
                              register={register2("brand", {
                                required: 'Please select brand.'
                              })}
                            />
                          </div>
                        </div>
                        <div className="col-lg-4 col-sm-6 col-12">
                          <div className="mb-3">
                            <SelectField
                              label={"Category"}
                              options={categories}
                              selected={selectedCategory}
                              onSelect={(value) => setSelectedCategory(value)}
                              error={errors2?.category?.message}
                              register={register2("category", {
                                required: 'Please select category.'
                              })}
                            />
                          </div>
                        </div>
                        <div className="col-lg-4 col-sm-6 col-12">
                          <div className="mb-3">
                            <SelectField
                              disabled={selectedBrand === null}
                              label={"Product"}
                              options={modalProducts}
                              selected={selectedModalProduct}
                              onSelect={(value) => setSelectedModalProduct(value)}
                              error={errors2?.product?.message}
                              register={register2("product", {
                                required: 'Please select product.'
                              })}
                            />
                          </div>
                        </div>
                        {modalVariants?.length > 0 &&
                          <Fragment>
                            {modalVariants.map((item, index) => (
                              <div key={index} className="col-lg-4 col-sm-6 col-12">
                                <div className="mb-3 add-product">
                                  <label className="form-label">{item?.name}</label>
                                  <Controller
                                    control={control2}
                                    name={`variant-${item.name}`}
                                    rules={{
                                      required: `Select ${item?.name}`,
                                    }}
                                    render={({ field }) => (
                                      <Select
                                        {...field}
                                        getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                        size="large"
                                        style={{ width: '100%' }}
                                        options={item?.options}
                                        onChange={(value) => {
                                          field.onChange(value);
                                          handleSelectVariant(item.name, value);
                                        }}
                                      />
                                    )}
                                  />
                                </div>
                              </div>
                            ))}
                          </Fragment>
                        }
                        <div className="col-lg-4 col-sm-6 col-12">
                          <div className="mb-3">
                            <InputField
                              label="Quantity"
                              error={errors2?.quantity?.message}
                              register={register2("quantity", {
                                required: 'Please enter quantity.'
                              })}
                            />
                          </div>
                        </div>
                        <div className="col-lg-4 col-sm-6 col-12">
                          <div className="mb-3">
                            <InputField
                              disabled={true}
                              label="Reg Price"
                              error={errors2?.regPrice?.message}
                              register={register2("regPrice", {
                                // required: 'Please enter reg price.'
                              })}
                            />
                          </div>
                        </div>
                        <div className="col-lg-4 col-sm-6 col-12">
                          <div className="mb-3">
                            <InputField
                              label="Sale Price"
                              error={errors2?.salePrice?.message}
                              register={register2("salePrice", {
                                required: 'Please enter sale price.'
                              })}
                            />
                          </div>
                        </div>
                        <div className="col-lg-7 col-sm-12 col-12">
                          <div className="mb-3">
                            <InputField
                              label="Description"
                              inputType="textarea"
                              placeholder="Type Description"
                              register={register2("description")}
                            />
                          </div>
                        </div>
                        {selectedModalVariant !== null &&
                          <div className="col-lg-12 col-sm-12 col-12">
                            <div className="mb-3">
                              Total Available Quantity: {selectedModalVariant?.totalQuantity || 0}
                              {selectedModalVariant?.totalOnHold > 0 && (
                                <span className="text-muted ms-2">
                                  ({selectedModalVariant.totalOnHold} on hold)
                                </span>
                              )}
                            </div>
                          </div>
                        }
                      </div>
                      <div className="modal-footer-btn">
                        <button
                          id="modalClose"
                          type="button"
                          className="btn btn-cancel me-2"
                          onClick={handleCloseProductSelector}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn-submit"
                          type="submit"
                        >
                          Select Product
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ zIndex: 99990 }} onClick={handleCloseProductSelector}></div>
        </div>,
        document.body
      )}

      {success && <div style={{ color: 'green', marginTop: 14, fontSize: 17 }}>{success}</div>}
    </div>
  );
};

export default ExchangeForm;